import type { CSSProperties, FC, ReactNode } from 'react'

import {
    BrainCircuit,
    Eye,
    Fingerprint,
    KeyRound,
    ListChecks,
    LockKeyhole
} from 'lucide-react'

import { Eyebrow } from '@/components/generals/eyebrow'
import { Container } from '@/components/generals/container'
import { RevealText } from '@/components/generals/reveal-text'

const ITEMS = [
    {
        title: 'Read-only by design',
        body: 'Veyra connects with view-only access. It can see your numbers but can never move money or change your records.',
        Icon: Eye
    },
    {
        title: 'Encrypted everywhere',
        body: 'Financial data is encrypted in transit and at rest, and connection credentials are never stored on our servers.',
        Icon: LockKeyhole
    },
    {
        title: 'Granular permissions',
        body: 'Decide exactly who sees payroll, who approves budgets and who can export reports, down to the entity.',
        Icon: KeyRound
    },
    {
        title: 'Complete audit trail',
        body: 'Every sync, automation and edit is logged with who, what and when, ready for your auditors.',
        Icon: ListChecks
    },
    {
        title: 'Single sign-on',
        body: 'Enforce SSO and SAML on Enterprise, with automatic user provisioning and offboarding.',
        Icon: Fingerprint
    },
    {
        title: 'Private by default',
        body: 'Your financial data is never used to train models for other customers. Your numbers stay yours.',
        Icon: BrainCircuit
    }
]

export const SecurityGrid: FC = (): ReactNode => {
    return (
        <section id='security' aria-labelledby='security-title' className='bg-paper'>
            <Container className='py-28 md:py-36'>
                <div className='grid gap-10 lg:grid-cols-12 lg:items-end'>
                    <div className='lg:col-span-7'>
                        <Eyebrow>Security and control</Eyebrow>

                        <RevealText
                            as='h2'
                            id='security-title'
                            text='Built for the most sensitive numbers you have.'
                            highlight={['most sensitive numbers']}
                            highlightClassName='text-stone'
                            className='mt-6 font-heading text-display-lg font-medium text-burgundy'
                        />
                    </div>

                    <p data-reveal='' className='text-lg leading-relaxed text-stone-dark lg:col-span-4 lg:col-start-9'>
                        Finance data deserves more care than any other. Security is part of the foundation, not a feature added later.
                    </p>
                </div>

                <ul className='mt-16 grid border-l border-t border-burgundy/10 sm:grid-cols-2 lg:grid-cols-3'>
                    {ITEMS.map(({ title, body, Icon }, index) => (
                        <li
                            key={title}
                            data-reveal=''
                            className='border-b border-r border-burgundy/10 p-8 md:p-10'
                            style={{ '--reveal-delay': `${(index % 3) * 90}ms` } as CSSProperties}
                        >
                            <span className='flex size-11 items-center justify-center rounded-full bg-burgundy text-gold'>
                                <Icon aria-hidden='true' className='size-5' />
                            </span>

                            <h3 className='mt-8 text-lg font-medium text-burgundy'>{title}</h3>
                            <p className='mt-3 leading-relaxed text-stone-dark'>{body}</p>
                        </li>
                    ))}
                </ul>
            </Container>
        </section>
    )
}
